import * as React from 'react';

import * as ramda from 'ramda';

import * as Schema from 'nutshell-core/schema';
import type {Assignable} from 'nutshell-core/types';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import type {CustomOption} from 'shells/assignee-select/assignee-select';
import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {Label} from 'shells/form/label';

import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import {Checkbox} from '../fields/checkbox';
import {ScrollableSection} from '../scrollable-section';

import {reorder} from './reorder';
import {SpecialCaseList} from './special-case-list';
import {SubformSpecialCase} from './subform-special-case';
import type {
    UserAssignmentAutomation,
    DefaultAssignementAutomation,
} from './user-assignments-types';

import styles from '../sales-process-form.module.css';

const UNASSIGN_OPTION: CustomOption = {id: 'UNASSIGN', name: 'Unassign lead'};

export type UserAssignmentsFormData = {
    defaultAutomation: DefaultAssignementAutomation | null | undefined;
    onlyUnassigned: boolean;
    specialCases: UserAssignmentAutomation[];
};

export type UserAssignmentsInitialValues = {
    defaultAutomation: DefaultAssignementAutomation | null | undefined;
    onlyUnassigned: boolean;
    specialCases: UserAssignmentAutomation[];
};

type Props = {
    entityId: string | null | undefined;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => void;
    onDelete: () => Promise<any>;
    onSaveForm: (arg1: UserAssignmentsFormData) => Promise<any>;
    initialValues: UserAssignmentsInitialValues;
    assignables: Assignable[];
    schema: Schema.Properties;
};

type State = {
    defaultAutomation: DefaultAssignementAutomation | null | undefined;
    onlyUnassigned: boolean;
    specialCases: UserAssignmentAutomation[];
    isAddingSpecialCase: boolean;
};

function getRoundRobin(
    automation:
        | UserAssignmentAutomation
        | null
        | undefined
        | DefaultAssignementAutomation
        | null
        | undefined
): boolean {
    if (!automation || !automation.assignee) return false;

    return Boolean(automation.shouldRoundRobin);
}

function shouldShowRoundRobin(
    automation:
        | UserAssignmentAutomation
        | null
        | undefined
        | DefaultAssignementAutomation
        | null
        | undefined
) {
    if (!automation || !automation.assignee) return false;

    return automation.assignee.__typename === 'Team';
}

export class FormUserAssignmentsComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        const {defaultAutomation, onlyUnassigned, specialCases} = props.initialValues;

        this.state = {
            defaultAutomation,
            isAddingSpecialCase: false,
            onlyUnassigned,
            specialCases,
        };
    }

    render() {
        const showRoundRobin = shouldShowRoundRobin(this.state.defaultAutomation);
        const shouldUnassign = Boolean(
            this.state.defaultAutomation && this.state.defaultAutomation.shouldUnassign
        );
        let defaultAssigneeId =
            this.state.defaultAutomation &&
            this.state.defaultAutomation.assignee &&
            this.state.defaultAutomation.assignee.id;
        if (shouldUnassign) {
            defaultAssigneeId = UNASSIGN_OPTION.id;
        }

        return (
            <SalesAutomationSidebarFormWrapper
                id={this.props.entityId}
                onSaveForm={this.saveForm}
                onDelete={{
                    deleteText: 'Remove assignment',
                    deleteConfirmationTitle: 'Are you sure you want to remove this assignment?',
                    onDeleteConfirm: this.handleDeleteConfirm,
                }}
            >
                <div className={styles['form-input']}>
                    <Label htmlFor='AssigneePicker'>Assign leads that enter this stage to…</Label>
                    <AssigneeSelect
                        inputId='AssigneePicker'
                        onChange={this.handleAssigneeChange}
                        value={defaultAssigneeId}
                        options={this.props.assignables}
                        customOptions={[UNASSIGN_OPTION]}
                    />
                </div>
                <div className={styles['form-input']}>
                    {showRoundRobin ? (
                        <Checkbox
                            label='Round-robin'
                            checked={getRoundRobin(this.state.defaultAutomation)}
                            onChange={this.handleRoundRobinChange}
                        />
                    ) : null}
                    <Checkbox
                        label='Only assign leads that aren’t already assigned'
                        checked={shouldUnassign ? false : this.state.onlyUnassigned}
                        disabled={shouldUnassign}
                        onChange={this.handleOnlyUnassignedChange}
                    />
                </div>
                <ScrollableSection hasTopBorder={true}>
                    {/* @ts-expect-error */}
                    <h4 className={styles.label}>Special cases</h4>
                    <p className={styles.description}>
                        You can also assign leads based on criteria like territory or market. If
                        more than one rule is met, the first matching rule will be applied. The
                        default assignee is applied when none of these special cases are met.
                    </p>
                    {this.state.isAddingSpecialCase ? (
                        <SubformSpecialCase
                            assignables={this.props.assignables}
                            schema={this.props.schema}
                            onApplySpecialCase={this.handleApplySpecialCase}
                            onCancelSpecialCase={this.handleCancelSpecialCase}
                        />
                    ) : (
                        <SpecialCaseList
                            specialCases={this.state.specialCases}
                            onClickAdd={this.handleAddSpecialCase}
                            onReorder={this.handleSpecialCaseReorder}
                            onRemove={this.handleSpecialCaseRemoval}
                        />
                    )}
                </ScrollableSection>
                <CrossBrowserHiddenFormSubmit inputRef={this.props.formSubmitBtnRef} />
            </SalesAutomationSidebarFormWrapper>
        );
    }

    handleAssigneeChange = (newAssigneeId?: string | null) => {
        if (!newAssigneeId) {
            // @ts-expect-error
            this.setState((prevState: State) => ({
                defaultAutomation: {
                    ...prevState.defaultAutomation,
                    shouldUnassign: false,
                    assignee: null,
                },
            }));
        } else if (newAssigneeId === UNASSIGN_OPTION.id) {
            this.handleUnassignSelected();
        } else {
            const assignee: any | null | undefined = ramda.find(ramda.propEq('id', newAssigneeId))(
                this.props.assignables
            );
            if (assignee) {
                assignee.__typename = assignee.type === 'teams' ? 'Team' : 'User';
                // @ts-expect-error
                this.setState((prevState: State) => ({
                    defaultAutomation: {
                        ...prevState.defaultAutomation,
                        shouldUnassign: false,
                        assignee,
                    },
                }));
            }
        }
    };

    handleUnassignSelected = () => {
        this.setState({
            defaultAutomation: {
                assignee: {
                    id: UNASSIGN_OPTION.id,
                    name: '',
                    isEnabled: true,
                    __typename: 'User',
                },
                shouldUnassign: true,
                shouldRoundRobin: false,
            },
            onlyUnassigned: false,
        });
    };

    handleRoundRobinChange = (shouldRoundRobin: boolean) => {
        // @ts-expect-error
        this.setState((prevState: State) => ({
            defaultAutomation: {
                ...prevState.defaultAutomation,
                shouldRoundRobin,
            },
        }));
    };

    handleOnlyUnassignedChange = (onlyUnassigned: boolean) => {
        this.setState({onlyUnassigned});
    };

    handleAddSpecialCase = () => {
        this.setState({isAddingSpecialCase: true});
    };

    handleApplySpecialCase = (newCase: UserAssignmentAutomation) => {
        this.setState((prevState) => ({
            isAddingSpecialCase: false,
            specialCases: prevState.specialCases.concat(newCase),
        }));
    };

    handleCancelSpecialCase = () => {
        this.setState({isAddingSpecialCase: false});
    };

    handleSpecialCaseReorder = (fromIndex: number, toIndex: number) => {
        this.setState((prevState) => {
            const specialCases = reorder(prevState.specialCases, fromIndex, toIndex);

            return {
                ...prevState,
                specialCases,
            };
        });
    };

    handleSpecialCaseRemoval = (index: number) => {
        this.setState((prevState) => {
            const specialCases = ramda.remove(index, 1, prevState.specialCases);

            return {...prevState, specialCases};
        });
    };

    handleDeleteConfirm = () => {
        return this.props.onDelete();
    };

    saveForm = () => {
        const {defaultAutomation, onlyUnassigned, specialCases} = this.state;

        return this.props.onSaveForm({
            defaultAutomation,
            onlyUnassigned,
            specialCases,
        });
    };
}
